import { Component, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';

import { Appearance } from '@entities/Appearance';
import { Configuration } from '@entities/Configuration';
import { Design } from '@entities/Design';
import { DynamicFormValue } from '@entities/DynamicFormValue';
import { Question, QuestionSegment } from '@entities/Question';
import { QuestionResponse } from '@entities/QuestionResponse';

@Component({
  tag: 'wog-question-section',
})
export class QuestionSection {
  @Prop() questions: Question[];
  @Prop() design: Design;
  @Prop() themeColor: string;
  @Prop() appearance: Appearance;
  @Prop() configuration: Configuration;
  @Prop() segment: QuestionSegment;
  @State() listOfQuestions: Question[];
  @State() currentQuestions: Question[];
  @State() registry: Record<string, QuestionResponse>;
  @State() requiredQuestions: string[] = [];
  @State() currentStep = 0;
  @State() validateStatus = false;
  @State() responses: QuestionResponse[];
  @Event() feedbackCancel: EventEmitter;
  @Event() wizardComplete: EventEmitter;
  @Event() questionComplete: EventEmitter;

  componentWillLoad() {
    this.listOfQuestions = this.questions
      .filter(e => e.questionSegment === this.segment)
      .sort((a, b) => a.index - b.index);
    this.currentQuestions = [this.listOfQuestions[this.currentStep]];
    const { registry, requiredQuestions } = this.getRegistry(this.currentQuestions);
    this.registry = registry;
    this.requiredQuestions = requiredQuestions;
  }

  private handleCancel = () => {
    this.feedbackCancel.emit();
  };

  private handleSubmit = (event: Event) => {
    event.preventDefault();
    if (this.validateStatus) {
      const values = this.responses.map(({ index, questionId, questionType, value }) => ({
        questionNumber: index,
        questionId,
        questionType,
        response: value,
      }));
      this.questionComplete.emit({ values });
      this.moveToNextStep();
      this.clearFormStatus();
    }
  };

  private handleSkip = () => {
    const values = this.currentQuestions.map(({ index, questionId, questionType }) => ({
      questionNumber: index,
      questionId,
      questionType,
      response: null,
    }));
    this.questionComplete.emit({ values });
    this.moveToNextStep();
    this.clearFormStatus();
  };

  private handleValidStatusChange = (event: CustomEvent<DynamicFormValue>) => {
    this.validateStatus = event.detail.status;
    this.responses = event.detail.responses;
  };

  private getRegistry(questions: Question[]) {
    const registry = {};
    let requiredQuestions: string[] = [];
    for (const question of questions) {
      const { hasOther, index, questionId, questionType, isRequired } = question;
      registry[questionId] = {
        hasOther,
        index,
        questionId,
        questionType,
        isRequired,
        isValid: false,
        value: null,
      };

      requiredQuestions = isRequired ? [...requiredQuestions, questionId] : requiredQuestions;
    }
    return {
      registry,
      requiredQuestions,
    };
  }

  private moveToNextStep() {
    if (this.currentStep + 1 < this.listOfQuestions.length) {
      this.currentStep = this.currentStep + 1;
      this.currentQuestions = [this.listOfQuestions[this.currentStep]];
      const { registry, requiredQuestions } = this.getRegistry(this.currentQuestions);
      this.registry = registry;
      this.requiredQuestions = requiredQuestions;
    } else {
      this.wizardComplete.emit();
    }
  }

  private clearFormStatus() {
    this.validateStatus = false;
  }

  private renderQuestion(questions: Question[]) {
    const submitButtonStyles = {
      backgroundColor: this.themeColor,
      color: this.appearance === Appearance.Light ? 'white' : '#333333',
    };
    return [
      <div class="wog--w-full">
        <wog-dynamic-form
          onValidStatusChange={this.handleValidStatusChange}
          registry={this.registry}
          requiredQuestions={this.requiredQuestions}
          questions={questions}
        />
      </div>,
      <div class="wog--flex wog--w-full wog--items-center wog--justify-between wog--pt-4">
        <div>
          <p class="wog--text-foundation">
            {this.currentStep + 1} of {this.listOfQuestions.length}
          </p>
        </div>
        <div class="wog--flex wog--items-center wog--justify-around">
          {!questions[0].isRequired && (
            <button
              type="button"
              class="wog--mx-5 wog--border-0 wog--bg-transparent wog--text-lg wog--font-bold"
              onClick={this.handleSkip}>
              Skip
            </button>
          )}
          <button
            type="submit"
            id="wogSubmitForm"
            onClick={this.handleSubmit}
            disabled={!this.validateStatus}
            style={submitButtonStyles}
            class={{
              'wog--button': true,
              'wog--cursor-pointer': this.validateStatus,
              'wog--opacity-50': !this.validateStatus,
              'wog--cursor-not-allowed': !this.validateStatus,
            }}>
            NEXT
          </button>
        </div>
      </div>,
    ];
  }

  render() {
    const { currentQuestions } = this;
    return (
      <Host class="wog--tab-view-container wog--questions-custom wog--mobile-full-position wog--flex wog--flex-col">
        <wog-close-button isWhite={false} onClose={this.handleCancel} />
        <div class="wog--question-animation wog--flex wog--flex-col wog--items-center wog--justify-center wog--py-4 wog--px-4">
          {this.renderQuestion(currentQuestions)}
        </div>
      </Host>
    );
  }
}
